// @ts-strict-ignore
import { useMutation, useQuery } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { DashboardCard } from "@dashboard/components/Card";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import { Select } from "@dashboard/components/Select";
import TableCellHeader from "@dashboard/components/TableCellHeader";
import useNotifier from "@dashboard/hooks/useNotifier";
import {
  Chip,
  IconButton,
  LinearProgress,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import DeleteIcon from "@material-ui/icons/Delete";
import DescriptionIcon from "@material-ui/icons/Description";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ImageIcon from "@material-ui/icons/Image";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import VideocamIcon from "@material-ui/icons/Videocam";
import { Box, Button, Option, Skeleton, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import { WhatsAppMedia, WhatsAppMediaType } from "../types";
import { WHATSAPP_MEDIA_DELETE_MUTATION, WHATSAPP_MEDIA_UPLOAD_MUTATION } from "../waMutations";
import { WHATSAPP_MEDIA_FILES_QUERY } from "../waQueries";

const TYPE_FILTER_OPTIONS: { label: string; value: string }[] = [
  { label: "All Media", value: "ALL" },
  { label: "Images", value: "IMAGE" },
  { label: "Videos", value: "VIDEO" },
  { label: "Documents & PDFs", value: "DOCUMENT" },
];

const MEDIA_TYPE_CHOICES: Option[] = [
  { label: "Auto Detect from File Extension", value: "AUTO" },
  { label: "Image (JPG, PNG, WebP)", value: "IMAGE" },
  { label: "Video (MP4, MOV)", value: "VIDEO" },
  { label: "Document (PDF, Word, Excel)", value: "DOCUMENT" },
];

export const MediaLibraryTab: React.FC = () => {
  const notify = useNotifier();
  const [typeFilter, setTypeFilter] = useState<string>("ALL");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WhatsAppMedia | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Upload dialog form state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedMediaType, setSelectedMediaType] = useState<string>("AUTO");

  // Query media files
  const { data, loading, refetch } = useQuery(WHATSAPP_MEDIA_FILES_QUERY, {
    variables: {
      mediaType: typeFilter === "ALL" ? undefined : typeFilter,
    },
    fetchPolicy: "cache-and-network",
  });

  const mediaFiles: WhatsAppMedia[] = data?.whatsappMediaFiles || [];

  // Upload mutation
  const [uploadMedia, { loading: uploading }] = useMutation(WHATSAPP_MEDIA_UPLOAD_MUTATION, {
    onCompleted: res => {
      if (res.whatsappMediaUpload?.errors?.length) {
        notify({ status: "error", text: res.whatsappMediaUpload.errors[0].message });
      } else {
        notify({ status: "success", text: "Media file uploaded successfully" });
        setUploadDialogOpen(false);
        setSelectedFile(null);
        setSelectedMediaType("AUTO");
        refetch();
      }
    },
    onError: err => {
      notify({ status: "error", text: err.message });
    },
  });

  // Delete mutation
  const [deleteMedia, { loading: deleting }] = useMutation(WHATSAPP_MEDIA_DELETE_MUTATION, {
    onCompleted: res => {
      if (res.whatsappMediaDelete?.errors?.length) {
        notify({ status: "error", text: res.whatsappMediaDelete.errors[0].message });
      } else {
        notify({ status: "success", text: "Media file deleted" });
        setDeleteTarget(null);
        refetch();
      }
    },
    onError: err => {
      notify({ status: "error", text: err.message });
    },
  });

  const handleCopyLink = (item: WhatsAppMedia) => {
    if (!item.fileUrl) return;
    navigator.clipboard.writeText(item.fileUrl);
    setCopiedId(item.id);
    notify({ status: "success", text: `Link copied to clipboard: ${item.fileName}` });
    setTimeout(() => {
      setCopiedId(prev => (prev === item.id ? null : prev));
    }, 2500);
  };

  const handleStartUpload = () => {
    if (!selectedFile) {
      notify({ status: "error", text: "Please select a file to upload" });
      return;
    }
    uploadMedia({
      variables: {
        file: selectedFile,
        mediaType: selectedMediaType === "AUTO" ? undefined : selectedMediaType,
      },
    });
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes || bytes === 0) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getTypeChip = (type: WhatsAppMediaType) => {
    switch (type) {
      case "IMAGE":
        return (
          <Chip
            size="small"
            icon={<ImageIcon style={{ fontSize: 14 }} />}
            label="Image"
            style={{ backgroundColor: "#dcfce7", color: "#166534" }}
          />
        );
      case "VIDEO":
        return (
          <Chip
            size="small"
            icon={<VideocamIcon style={{ fontSize: 14 }} />}
            label="Video"
            style={{ backgroundColor: "#e0f2fe", color: "#0369a1" }}
          />
        );
      case "DOCUMENT":
        return (
          <Chip
            size="small"
            icon={<DescriptionIcon style={{ fontSize: 14 }} />}
            label="Document / PDF"
            style={{ backgroundColor: "#ffedd5", color: "#9a3412" }}
          />
        );
      default:
        return <Chip size="small" label={type} />;
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      {/* Information Banner */}
      <Box
        padding={3}
        style={{
          backgroundColor: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, color: "#1e293b" }}>
            WhatsApp Campaign Media Assets Bucket Storage
          </Typography>
          <Typography variant="body2" color="textSecondary">
            All files uploaded here are organized in dedicated S3 storage folders:{" "}
            <code>whatsapp_campaigns/images/</code>, <code>whatsapp_campaigns/videos/</code>, and{" "}
            <code>whatsapp_campaigns/documents/</code>. Click <strong>Copy Link</strong> to paste direct URLs into campaign template headers or messages.
          </Typography>
        </Box>
        <Button onClick={() => setUploadDialogOpen(true)}>+ Upload Media File</Button>
      </Box>

      {/* Filter and Library Table */}
      <DashboardCard>
        <Box padding={3} display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" gap={1}>
            {TYPE_FILTER_OPTIONS.map(opt => (
              <Chip
                key={opt.value}
                label={opt.label}
                clickable
                color={typeFilter === opt.value ? "primary" : "default"}
                onClick={() => setTypeFilter(opt.value)}
                variant={typeFilter === opt.value ? "default" : "outlined"}
              />
            ))}
          </Box>
          <Text size={2} color="default2">
            {mediaFiles.length} file{mediaFiles.length === 1 ? "" : "s"} found
          </Text>
        </Box>

        {loading ? (
          <Box padding={4}>
            <Skeleton />
          </Box>
        ) : (
          <ResponsiveTable>
            <TableHead>
              <TableRow>
                <TableCellHeader style={{ width: 60 }}>Preview</TableCellHeader>
                <TableCellHeader>File Name</TableCellHeader>
                <TableCellHeader>Type</TableCellHeader>
                <TableCellHeader>File Size</TableCellHeader>
                <TableCellHeader>Uploaded Date</TableCellHeader>
                <TableCellHeader textAlign="right" style={{ width: 230, minWidth: 210 }}>
                  Actions
                </TableCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {mediaFiles.map(item => {
                const isCopied = copiedId === item.id;
                return (
                  <TableRow key={item.id} hover>
                    <TableCell style={{ width: 60 }}>
                      {item.mediaType === "IMAGE" && item.fileUrl ? (
                        <img
                          src={item.fileUrl}
                          alt={item.fileName}
                          style={{
                            width: 44,
                            height: 44,
                            objectFit: "cover",
                            borderRadius: 6,
                            border: "1px solid #e2e8f0",
                          }}
                        />
                      ) : item.mediaType === "VIDEO" ? (
                        <Box
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 6,
                            backgroundColor: "#e0f2fe",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#0284c7",
                          }}
                        >
                          <VideocamIcon fontSize="small" />
                        </Box>
                      ) : (
                        <Box
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 6,
                            backgroundColor: "#ffedd5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#ea580c",
                          }}
                        >
                          <DescriptionIcon fontSize="small" />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">{item.fileName}</Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{
                          display: "block",
                          maxWidth: 320,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontFamily: "monospace",
                        }}
                      >
                        {item.fileUrl}
                      </Typography>
                    </TableCell>
                    <TableCell>{getTypeChip(item.mediaType)}</TableCell>
                    <TableCell>{formatFileSize(item.fileSize)}</TableCell>
                    <TableCell>{new Date(item.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="right" style={{ width: 230, minWidth: 210 }}>
                      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} style={{ flexWrap: "nowrap" }}>
                        <Tooltip title={isCopied ? "Link Copied!" : "Copy Public Link"}>
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => handleCopyLink(item)}
                            style={{
                              minWidth: 108,
                              whiteSpace: "nowrap",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 6,
                              padding: "4px 10px",
                              backgroundColor: isCopied ? "#dcfce7" : undefined,
                              borderColor: isCopied ? "#86efac" : undefined,
                              color: isCopied ? "#166534" : undefined,
                            }}
                          >
                            {isCopied ? (
                              <CheckIcon style={{ fontSize: 15 }} />
                            ) : (
                              <FileCopyIcon style={{ fontSize: 15 }} />
                            )}
                            <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>
                              {isCopied ? "Copied" : "Copy Link"}
                            </span>
                          </Button>
                        </Tooltip>

                        {item.fileUrl && (
                          <IconButton
                            size="small"
                            title="Open in new tab"
                            onClick={() => window.open(item.fileUrl, "_blank")}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        )}

                        <IconButton
                          size="small"
                          title="Delete File"
                          onClick={() => setDeleteTarget(item)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>

                  </TableRow>
                );
              })}
              {mediaFiles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box padding={5} display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <Text color="default2">No media files uploaded yet in this category.</Text>
                      <Button variant="secondary" size="small" onClick={() => setUploadDialogOpen(true)}>
                        Upload Your First Campaign Media Asset
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </ResponsiveTable>
        )}
      </DashboardCard>

      {/* Upload Media Dialog */}
      <ActionDialog
        open={uploadDialogOpen}
        size="sm"
        title="Upload Campaign Media to Bucket"
        confirmButtonLabel="Upload to Bucket"
        confirmButtonState={uploading ? "loading" : "default"}
        onClose={() => {
          if (!uploading) {
            setUploadDialogOpen(false);
            setSelectedFile(null);
          }
        }}
        onConfirm={handleStartUpload}
      >
        <Box display="flex" flexDirection="column" gap={3} width="100%">
          <Text size={2} color="default2">
            Select an image (JPG, PNG, WebP), video (MP4), or document (PDF) to store in the dedicated WhatsApp campaign folder in our S3 bucket.
          </Text>

          <Select
            label="Media Asset Type"
            name="mediaType"
            value={selectedMediaType}
            options={MEDIA_TYPE_CHOICES}
            onChange={({ target }) => setSelectedMediaType(target.value as any)}
          />

          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            padding={3}
            style={{
              border: "2px dashed #cbd5e1",
              borderRadius: 8,
              backgroundColor: "#f8fafc",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <input
              type="file"
              id="wa-media-file-input"
              style={{ display: "none" }}
              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
              onChange={e => {
                if (e.target.files?.[0]) {
                  setSelectedFile(e.target.files[0]);
                }
              }}
            />
            <label htmlFor="wa-media-file-input" style={{ cursor: "pointer", width: "100%", textAlign: "center" }}>
              <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                <Typography variant="body2" style={{ fontWeight: 600, color: "#2563eb" }}>
                  {selectedFile ? selectedFile.name : "Click here to choose a file"}
                </Typography>
                {selectedFile ? (
                  <Typography variant="caption" color="textSecondary">
                    File Size: {formatFileSize(selectedFile.size)}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="textSecondary">
                    Supports JPG, PNG, WebP, MP4, PDF, and Documents up to 100MB
                  </Typography>
                )}
              </Box>
            </label>
          </Box>

          {uploading && (
            <Box width="100%">
              <LinearProgress />
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4, textAlign: "center" }}>
                Uploading to S3 bucket dedicated WhatsApp folder...
              </Typography>
            </Box>
          )}
        </Box>
      </ActionDialog>

      {/* Delete Confirmation Dialog */}
      <ActionDialog
        open={!!deleteTarget}
        title="Delete Media Asset?"
        variant="delete"
        confirmButtonLabel="Delete"
        confirmButtonState={deleting ? "loading" : "default"}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            deleteMedia({ variables: { id: deleteTarget.id } });
          }
        }}
      >
        <Text>
          Are you sure you want to delete <strong>{deleteTarget?.fileName}</strong> from the WhatsApp campaign bucket? Messages using this file URL might no longer load the asset.
        </Text>
      </ActionDialog>
    </Box>
  );
};

export default MediaLibraryTab;
